import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import React, { useEffect } from "react"
import { Button, Col, Row } from "react-bootstrap"
import { FormattedMessage, FormattedNumber } from "react-intl"
import { useDispatch, useSelector } from "react-redux"
import Icon from "../../../common/icon/icon.component"
import Image from "../../../common/image/image.component"
import LabelBadge from "../../../common/label/badge.component"
import StandardList from "../../../common/listing/list.component"
import { removeBundleItem, updateBundleItem, updateProduct } from "../../../state/product/ProductActions"
import { getBundleItems } from "../../../state/product/ProductSelectors"
import { PRODUCT_UPDATE_OPTION } from "../../../state/product/Types"
import { BundleItem } from "../../../state/product/bundle/models/BundleItem"
import { handleTotalAmount, mapItemKeyToItems } from "../../../utils/bundle"
import "../styles/BundleItemsListStyles.css"

const BundleItemsListComponent = () => {
    const dispatch = useDispatch()
    const bundleItems = useSelector(state => getBundleItems(state))
    const hasKeys = bundleItems.every(i => i.itemKey !== undefined)

    useEffect(() => {
        dispatch(updateProduct({
            updateType: PRODUCT_UPDATE_OPTION.DEFAULT_UNIT_PRICE,
            value: String(handleTotalAmount(bundleItems))
        }))
    }, [])

    useEffect(() => {
        if(hasKeys) {
            dispatch(updateBundleItem(bundleItems))
        } else {
            const mapped = mapItemKeyToItems(bundleItems)
            dispatch(updateBundleItem(mapped))
        }
    }, [hasKeys])

    const handleRemoveProduct = (item: BundleItem) => {
        dispatch(removeBundleItem(item))
    }

    const handleQtyNumber = (value) => {
        const validatedValue =  /^[0-9]*\.?[0-9]*$/.test(value)
            ? value
            : "1"
        
        return validatedValue
    }

    const handleInputChange = (item: BundleItem, field: string, e) => {
        const newValue = handleQtyNumber(e.target.value)

        const itemsWithUpdates = bundleItems.map(i =>
            i.itemKey === item.itemKey
                ? {
                    ...item,
                    [field]: newValue
                }
                : i
        )
        dispatch(updateBundleItem(itemsWithUpdates)) 
    }

    const handleTotal = (i: BundleItem[]) => {
        return (<span>
            <FormattedMessage
                key="product.bundle.components.bundle_items_list_component.total"
                id="product.bundle.components.bundle_items_list_component.total"
                defaultMessage="Total: "
            />
            <FormattedNumber
                style="currency"
                currency={AppContext.getAccount().attributes.currency}
                value={handleTotalAmount(i)}
            />
        </span>)
    }

    const headers = {
        classNames: ["", "", "", "", "bundle-list-input-column", "price-panel-column"],
        data: [
            <FormattedMessage
                key="product.bundle.components.bundle_items_list_component.image"
                id="product.bundle.components.bundle_items_list_component.image"
                defaultMessage="Image"
            />,
            <FormattedMessage
                key="product.bundle.components.bundle_items_list_component.title"
                id="product.bundle.components.bundle_items_list_component.title"
                defaultMessage="Title"
            />,
            <FormattedMessage
                key="product.bundle.components.bundle_items_list_component.labels"
                id="product.bundle.components.bundle_items_list_component.labels"
                defaultMessage="Labels"
            />,
            <FormattedMessage
                key="product.bundle.components.bundle_items_list_component.unit_price"
                id="product.bundle.components.bundle_items_list_component.unit_price"
                defaultMessage="Cost"
            />,
            <FormattedMessage
                key="product.bundle.components.bundle_items_list_component.quantity"
                id="product.bundle.components.bundle_items_list_component.quantity"
                defaultMessage="Quantity"
            />,
            <FormattedMessage
                key="product.bundle.components.bundle_items_list_component.price"
                id="product.bundle.components.bundle_items_list_component.price"
                defaultMessage="Price"
            />
        ]
    }

    const rows = bundleItems?.map(item => {
        const image = item.imageIds?.length > 0 ?
            <Image key={item.productId} imageId={item.imageIds[0]} size="very-small" /> :
            <Icon value={"image"} style={{ fontSize: "30px" }} />

        const title = [] as JSX.Element[]
        title.push(<p key={item.productId} className="m-0">{item.title}</p>)

        const labels = item.labels?.map(label => {
            return <LabelBadge key={label.id} value={label} />
        })

        const unitCost = [] as JSX.Element[]
        unitCost.push(<p key={item.productId} className="m-0">{item.unitCost}</p>)

        const quantityInput = [] as JSX.Element[]
        quantityInput.push(<input
            name="quantity"
            value={item.quantity}
            onChange={e => handleInputChange(item, "quantity", e)}
            id={item.productId}
            key={item.productId}
            type="number"
            min={1}
            className="form-control"
        />)

        const pricePanel = [] as JSX.Element[]
        pricePanel.push(<div className="d-flex justify-content-between">
            <div className="px-2">
                <FormattedNumber
                    style="currency"
                    currency={AppContext.getAccount().attributes.currency}
                    value={item.unitPrice}
                />
            </div>
            <div>
                <Button
                    variant="danger"
                    size="sm"
                    id={item.productId}
                    onClick={() => handleRemoveProduct(item)}
                >
                    <Icon value={"trash"} className={"mr-1"} />
                </Button>
            </div>
        </div>)

        return {
            data: [
                image,
                title,
                labels,
                unitCost,
                quantityInput,
                pricePanel
            ]
        }
    })

    const footer = {
        classNames: [
            "bg-dark",
            "bg-dark",
            "bg-dark",
            "bg-dark",
            "bg-dark",
            "bg-dark text-white px-4"],
        data: ["" ,"", "", "", "", handleTotal(bundleItems)]
    }

    return <div>
        <Row>
            <Col>
                <StandardList
                    smallLine={true}
                    headers={headers}
                    rows={rows}
                    footer={footer}
                    className="row pt-3"
                />
            </Col>
        </Row>
    </div>
}

export default BundleItemsListComponent


// WEBPACK FOOTER //
// ./src/product/bundle/components/BundleItemsListComponent.tsx